import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useApolloClient } from 'react-apollo'

import { getWindow } from '@acorns/web-utils'

import {
  SESSION_INACTIVITY_DETECTED_SESSION_STORAGE_KEY,
  getTimeElapsedSinceLastSessionRefresh,
  hasSessionLastRefreshedTimestamp,
  isAuthenticated,
  logout,
} from 'utils/session'

import { SessionAboutToExpireModal } from './SessionAboutToExpireModal'
import { SessionHasExpiredModal } from './SessionHasExpiredModal'

const ONE_SECOND_IN_MILLISECONDS = 1000

// The interval at which to check on the session to determine whether we need to:
// - Display (or hide) the "session about to expire" modal.
// - Display the "session is expired" modal and close the session.
export const SESSION_TIMER_INTERVAL = 2 * ONE_SECOND_IN_MILLISECONDS

// The length of the session. This can be no longer than the expiration of either:
// - The expiration of the cookies storing the auth-related tokens.
// - The expiration of the auth-related tokens themselves on the backend.
export const SESSION_LENGTH = 10 * 60 * ONE_SECOND_IN_MILLISECONDS // 10 minutes in milliseconds

// The time left before the session expires that triggers the "session about to expire" modal.
export const SESSION_ABOUT_TO_EXPIRE_THRESHOLD =
  2 * 60 * ONE_SECOND_IN_MILLISECONDS // 2 minutes in milliseconds

export function SessionTimer() {
  const apolloClient = useApolloClient()
  const sessionTimerRef = useRef<ReturnType<typeof setInterval>>()

  const [timeSinceLastReset, setTimeSinceLastReset] = useState(0) // time in milliseconds

  const [showSessionAboutToExpireModal, setShowSessionAboutToExpireModal] =
    useState(false)
  const [showSessionHasExpiredModal, setShowSessionHasExpiredModal] =
    useState(false)

  const destroySession = useCallback(() => {
    return logout(apolloClient)
  }, [apolloClient])

  useEffect(function startSessionTimer() {
    if (isAuthenticated() && hasSessionLastRefreshedTimestamp()) {
      sessionTimerRef.current = setInterval(() => {
        setTimeSinceLastReset(getTimeElapsedSinceLastSessionRefresh())
      }, SESSION_TIMER_INTERVAL)
    }
  }, [])

  useEffect(
    function checkShouldShowSessionAboutToExpireModal() {
      const timeLeftInSession = SESSION_LENGTH - timeSinceLastReset

      if (timeLeftInSession >= SESSION_ABOUT_TO_EXPIRE_THRESHOLD) {
        window.sessionStorage.removeItem(
          SESSION_INACTIVITY_DETECTED_SESSION_STORAGE_KEY,
        )
        setShowSessionAboutToExpireModal(false)
      } else if (
        timeLeftInSession < SESSION_ABOUT_TO_EXPIRE_THRESHOLD &&
        timeLeftInSession > 0
      ) {
        window.sessionStorage.setItem(
          SESSION_INACTIVITY_DETECTED_SESSION_STORAGE_KEY,
          'true',
        )
        setShowSessionAboutToExpireModal(true)
      }
    },
    [timeSinceLastReset],
  )

  useEffect(
    function checkShouldShowSessionHasBeenExpiredModal() {
      const timeLeftInSession = SESSION_LENGTH - timeSinceLastReset

      if (timeLeftInSession <= 0) {
        setShowSessionHasExpiredModal(true)
      }
    },
    [timeSinceLastReset],
  )

  useEffect(
    function handleSessionExpiration() {
      if (showSessionHasExpiredModal) {
        destroySession()

        setTimeout(() => {
          getWindow().location.replace('/')
        }, 2000)
      }
    },
    [showSessionHasExpiredModal, destroySession],
  )

  if (showSessionHasExpiredModal) {
    return <SessionHasExpiredModal />
  }

  if (showSessionAboutToExpireModal) {
    return <SessionAboutToExpireModal />
  }

  return null
}
